import React, { useEffect, useRef, useState } from "react";
import { useNexOs } from "@/lib/nexOs";
import { useLabel } from "@/lib/dynamicEngine";
import { useAuth } from "@/lib/auth";
import { isSuperAdminUser, usePlacedItems } from "@/lib/placement";

/**
 * Tier 0 — Master Vertical Selector.
 * 3 pill tabs; switching re-skins the data context without remounting layout.
 */
export default function VerticalSelector() {
  const { schema, vertical, setVertical } = useNexOs();
  const kicker = useLabel("nds.selector.kicker", "// SELECT INDUSTRY VERTICAL");
  const { user } = useAuth();
  const placed = usePlacedItems();
  const limited = !isSuperAdminUser(user) && Array.isArray(user?.visible_cards);
  const items = (schema?.verticals || []).filter((v) => !limited || v.id === "all_engines" || placed.some((c) => c.vertical_id === v.id));
  const ref = useRef(null);
  const [slim, setSlim] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => {
      const head = document.querySelector('[data-testid="nds-header"]')?.getBoundingClientRect().bottom || 0;
      setSlim(el.getBoundingClientRect().bottom < head ? Math.round(head) : null);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => { window.removeEventListener("scroll", check); window.removeEventListener("resize", check); };
  }, []);

  return (
    <>
    {slim != null && items.length > 0 && (
      <div data-testid="nds-vertical-slim" style={{ top: slim }} className="fixed left-0 right-0 z-30 border-b border-white/10 bg-navy-900/95 backdrop-blur-md">
        <div className="md:hidden px-4 py-1.5 flex items-center gap-2">
          <span className="nds-label-dim shrink-0">ENGINE</span>
          <select value={vertical} onChange={(e) => setVertical(e.target.value)} data-testid="nds-vertical-slim-select"
            className="flex-1 min-w-0 bg-navy-950 border border-white/10 rounded-full px-3 py-1 font-mono uppercase tracking-[0.12em] text-white">
            {items.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
        </div>
        <div className="hidden md:flex w-full max-w-[1920px] mx-auto px-6 lg:px-10 py-1.5 items-center gap-2 overflow-x-auto">
          <span className="nds-label-dim shrink-0 mr-1">ENGINE</span>
          {items.map((v) => (
            <button key={v.id} type="button" data-testid={`nds-vertical-slim-${v.id}`} data-active={v.id === vertical} onClick={() => setVertical(v.id)}
              className={`shrink-0 px-3 py-1 rounded-full border font-mono text-[10px] tracking-[0.16em] uppercase transition-colors ${v.id === vertical ? "border-brand-cyan/70 bg-brand-cyan/15 text-white" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>{v.label}</button>
          ))}
        </div>
      </div>
    )}
    <section
      ref={ref}
      data-testid="nds-vertical-selector"
      className="w-full border-b border-white/8"
    >
      <div className="w-full max-w-[1920px] mx-auto px-6 lg:px-10 py-5 flex flex-col gap-3">
        <div className="nds-label-dim">{kicker}</div>
        <div className="flex flex-wrap gap-3">
          {items.map((v) => {
            const active = v.id === vertical;
            return (
              <button
                key={v.id}
                type="button"
                data-testid={`nds-vertical-tab-${v.id}`}
                data-active={active}
                onClick={() => setVertical(v.id)}
                className={`nds-tab ${active ? "nds-tab--active" : ""}`}
              >
                {v.label}
              </button>
            );
          })}
          {items.length === 0 && (
            <div className="nds-label-dim" data-testid="nds-vertical-empty">Loading verticals…</div>
          )}
        </div>
        {items.length > 0 && (
          <p
            className="font-mono text-[0.75rem] text-nex-chrome tracking-[0.04em]"
            data-testid="nds-vertical-tagline"
          >
            {(items.find((v) => v.id === vertical) || items[0])?.tagline}
          </p>
        )}
      </div>
    </section>
    </>
  );
}
